import React, { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft } from "react-icons/hi";
import type { ArtworkMedium } from "../../lib/artworkPaths";
import { galleryPathForMedium } from "../../lib/artworkPaths";
import { ArtworkImage } from "../ArtworkImage";
import { InquireArtworkLink } from "../InquireArtworkLink";
import { ArtworkShareButton } from "../ArtworkShareButton";

type ArtworkDetailLayoutProps = {
  medium: ArtworkMedium;
  artworkId: string;
  artworkName: string;
  eyebrow: string;
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  imageGallery?: ReactNode;
  specs?: ReactNode;
  links?: ReactNode;
  footer?: ReactNode;
  /** Renders beside the title — e.g. sold label on canvas works. */
  titleExtra?: ReactNode;
  /** Replaces the default inquire CTA when set (e.g. commission link for sold works). */
  primaryAction?: ReactNode;
};

export function ArtworkDetailLayout({
  medium,
  artworkId,
  artworkName,
  eyebrow,
  title,
  description,
  imageUrl,
  imageAlt,
  imageGallery,
  specs,
  links,
  footer,
  titleExtra,
  primaryAction,
}: ArtworkDetailLayoutProps) {
  const { t } = useTranslation();

  return (
    <main>
      <section className="section-container py-5 sm:py-12 lg:py-16">
        <Link
          to={galleryPathForMedium(medium)}
          className="mb-5 inline-flex items-center gap-2 text-sm text-canvas-600 transition-colors hover:text-canvas-950 sm:mb-8"
        >
          <HiArrowLeft className="h-4 w-4 shrink-0 rtl:rotate-180" />
          {t("common.backToGallery")}
        </Link>

        <div className="grid items-start gap-6 sm:gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="min-w-0 lg:sticky lg:top-24 lg:self-start">
            {imageGallery ?? (
              <div className="art-card overflow-hidden">
                <ArtworkImage src={imageUrl} alt={imageAlt} layout="masonry" />
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-col justify-center">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-accent">
              {eyebrow}
            </p>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 sm:mt-3">
              <h1 className="font-display text-2xl font-semibold leading-tight text-canvas-950 sm:text-4xl lg:text-5xl">
                {title}
              </h1>
              {titleExtra}
            </div>
            <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-canvas-600 sm:mt-6 sm:text-base">
              {description}
            </p>
            {specs}
            {links}
            <div className="mt-5 flex flex-col gap-2.5 sm:mt-6 sm:flex-row sm:flex-wrap sm:gap-3">
              {primaryAction ?? (
                <InquireArtworkLink
                  medium={medium}
                  id={artworkId}
                  name={artworkName}
                  className="btn-accent w-full text-center sm:w-auto"
                />
              )}
              <ArtworkShareButton
                medium={medium}
                id={artworkId}
                title={title}
                text={description}
                className="btn-secondary w-full text-center sm:w-auto"
              />
              <Link
                to={galleryPathForMedium(medium)}
                className="btn-secondary w-full text-center sm:w-auto"
              >
                {t("home.featured.exploreGallery")}
              </Link>
            </div>
          </div>
        </div>
      </section>
      {footer}
    </main>
  );
}
